import { FieldUniqueIdentifier } from "../utils/types"

/**
 * @fileoverview Defines Field Unique Identifiers (FUIDs).
 * An FUID is a unique string key representing a logical piece of data across services.
 *
 * How to Define FUIDs:
 * 1. Use the exact Field Unique Id from the "Unified Data Dictionary" spreadsheet (e.g. _req_urgency).
 * 2. FUIDs must follow the pattern: <prefix>_<fuid cat>_<fuid path> (e.g. _req_AutoApproval).
 * 3. Each FUID must be globally unique.
 * 4. Type: Cast the string as FieldUniqueIdentifier.
 * 5. Add a mapping for each FUID in mappings.ts with service-specific paths.
 */
// REQUEST fields
export const REQUEST_ID = "_req_id" as FieldUniqueIdentifier
export const REQUEST_AUTO_APPROVAL =
  "_req_AutoApproval" as FieldUniqueIdentifier
export const SEQUENCE_NUMBER = "_req_sequenceNumber" as FieldUniqueIdentifier
export const REQUEST_CONSIGNOR =
  "_party_tdConsignor_identifier" as FieldUniqueIdentifier
export const REQUEST_CONSIGNEE =
  "_party_tdConsignee_identifier" as FieldUniqueIdentifier
export const REQUEST_URGENCY = "_req_urgency" as FieldUniqueIdentifier
export const REQUEST_TEMPLATE = "_template_code" as FieldUniqueIdentifier
export const REQUEST_COMMODITY = "_req_commodity_code" as FieldUniqueIdentifier
export const REQUEST_DESTINATION_COUNTRY =
  "_req_destinationCountry" as FieldUniqueIdentifier
export const REQUEST_EXPORTING_COUNTRY =
  "_req_exportingCountry" as FieldUniqueIdentifier
export const REQUEST_TRANSPORT_MODE_CODE =
  "_req_transportModeCode" as FieldUniqueIdentifier
export const REQUEST_CONVEYANCE_REFERENCE =
  "_req_conveyanceReference" as FieldUniqueIdentifier
export const REQUEST_TRANSPORT_LOCAL_CARRIER =
  "_req_tdLocalCarrier" as FieldUniqueIdentifier
export const REQUEST_TRANSPORT_LOCAL_CARRIER_REFERENCE =
  "_req_tdLocalCarrierReference" as FieldUniqueIdentifier
export const REQUEST_LOADING_PORT = "_req_loadingPort" as FieldUniqueIdentifier
export const REQUEST_FINAL_DESTINATION =
  "_req_finalDestination" as FieldUniqueIdentifier
export const REQUEST_TRANSPORT_DEPARTURE_DATE =
  "_req_departureDate" as FieldUniqueIdentifier
export const REQUEST_TRANSPORT_EXPECTED_ARRIVAL_DATE =
  "_req_expectedArrivalDate" as FieldUniqueIdentifier
export const REQUEST_UNOFFICIAL_INFORMATION =
  "_req_unofficialInformation" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_ID = "productId" as FieldUniqueIdentifier
export const PRODUCT_ELIGIBILITY_COUNTRIES =
  "_prod_eligibilityCountries" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_ATTRIBUTES_ORIGIN =
  "_prod_origin" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_ATTRIBUTES_ORIGIN_COUNTRIES =
  "_prod_originCountries" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_ATTRIBUTES_INSPECTION_TYPE =
  "_prod_inspectionType" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_BOTANICAL_NAME =
  "_prod_phytoBotanicalName" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_COMMON_NAME =
  "_prod_phytoCommonName" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_CLASS =
  "_prod_phytoClass" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_SUB_CLASS =
  "_prod_phytoSubclass" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_QUANTITY_TYPE =
  "_prod_phytoQuantityType" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_QUANTITY_VALUE =
  "_prod_phytoQuantity_value" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_QUANTITY_UNIT =
  "_prod_phytoQuantity_unit" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_VARIETY =
  "_prod_phytoVariety" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_APPROVED_SEED_CATEGORY =
  "_prod_phytoApprovedSeedCategory" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_PHYTO_LABEL_DESCRIPTION =
  "_prod_phytoLabelDescription" as FieldUniqueIdentifier
export const REQUEST_IMPORT_PERMIT_NUMBERS =
  "_req_importPermitNumbers" as FieldUniqueIdentifier
export const REQUEST_ATTR_LETTER_OF_CREDIT_REF_CODE =
  "_req_attr_letterOfCreditRefCode" as FieldUniqueIdentifier
export const REQUEST_ATTR_PHYTO_LOT_REFERENCE_NUMBER =
  "_req_attr_Lot" as FieldUniqueIdentifier
export const REQUEST_ATTR_PHYTO_PACKHOUSE =
  "_req_attr_phytoPackhouse" as FieldUniqueIdentifier
export const REQUEST_PRODUCT_ATTRIBUTES_SPECIES_COMMON_NAME =
  "_prod_commonNames" as FieldUniqueIdentifier
export const TEMPLATE_CODE = "_template_code" as FieldUniqueIdentifier
export const REQUEST_CARRIER_NAME = "_req_carrierName" as FieldUniqueIdentifier
export const REQUEST_DISCHARGE_PORT =
  "_req_dischargePort" as FieldUniqueIdentifier
export const REQUEST_INSPECTION_PORT =
  "_req_inspectionPort" as FieldUniqueIdentifier
export const PARTY_CONSIGNOR_ID =
  "_party_consignor_identifier" as FieldUniqueIdentifier
export const PARTY_IMPORTER_ID =
  "_party_importer_identifier" as FieldUniqueIdentifier
export const PARTY_IMPORTER_NAME =
  "_party_importer_name" as FieldUniqueIdentifier
export const PARTY_IMPORTER_ADDRESS_LINES =
  "_party_importer_addressLines" as FieldUniqueIdentifier
export const PARTY_IMPORTER_CITY =
  "_party_importer_city" as FieldUniqueIdentifier
export const PARTY_IMPORTER_POSTCODE =
  "_party_importer_postCode" as FieldUniqueIdentifier
export const PARTY_IMPORTER_COUNTRY =
  "_party_importer_country" as FieldUniqueIdentifier
export const PARTY_IMPORTER_STATE =
  "_party_importer_state" as FieldUniqueIdentifier
export const PARTY_IMPORT_REPRESENTATIVE =
  "_party_importer_representative" as FieldUniqueIdentifier
export const PARTY_DESTINATION_IDENTIFIER =
  "_party_destination_identifier" as FieldUniqueIdentifier
export const PARTY_DESTINATION_NAME =
  "_party_destination_name" as FieldUniqueIdentifier
export const PARTY_DESTINATION_ADDRESS =
  "_party_destination_addressLines" as FieldUniqueIdentifier
export const PARTY_DESTINATION_CITY =
  "_party_destination_city" as FieldUniqueIdentifier
export const PARTY_DESTINATION_POSTCODE =
  "_party_destination_postCode" as FieldUniqueIdentifier
export const PARTY_DESTINATION_COUNTRY =
  "_party_destination_country" as FieldUniqueIdentifier
export const PARTY_DESTINATION_STATE =
  "_party_destination_state" as FieldUniqueIdentifier
export const REQUEST_OFFICIAL_STATEMENT =
  "_req_officialStatement" as FieldUniqueIdentifier
export const REQUEST_SIGNING_OFFICE =
  "_req_signingOffice" as FieldUniqueIdentifier
export const REQUEST_APPROVED_EMAIL =
  "_req_approvedCertificateEmailAddresses" as FieldUniqueIdentifier
export const PARTY_THIRD_PARTY_CONSIGNOR_IDENTIFIER =
  "_party_thirdPartyConsignor_identifier" as FieldUniqueIdentifier //external consignor field for  phyto only
export const TRANSFER_THIRD_PARTY_IDENTIFIER =
  "_party_thirdParty_identifier" as FieldUniqueIdentifier // external consignor field for AP and Transfers
export const PRODUCT_DESCRIPTION = "_prod_description" as FieldUniqueIdentifier
export const PRODUCT_FIRST_EXPIRY_DATE =
  "_prod_firstExpiryDate" as FieldUniqueIdentifier
export const PRODUCT_LAST_EXPIRY_DATE =
  "_prod_lastExpiryDate" as FieldUniqueIdentifier
export const PRODUCT_CONTAINERS_ID =
  "_prod_containers_id" as FieldUniqueIdentifier
export const PRODUCT_CONTAINERS_NUMBER =
  "_prod_containers_number" as FieldUniqueIdentifier
export const PRODUCT_CONTAINERS_SEALS =
  "_prod_containers_seals" as FieldUniqueIdentifier
export const PRODUCT_GROSS_WEIGHT_VALUE =
  "_prod_grossWeight_value" as FieldUniqueIdentifier
export const PRODUCT_GROSS_WEIGHT_UNIT =
  "_prod_grossWeight_unit" as FieldUniqueIdentifier
export const PRODUCT_GROSS_VOLUME_VALUE =
  "_prod_grossVolume_value" as FieldUniqueIdentifier
export const PRODUCT_GROSS_VOLUME_UNIT =
  "_prod_grossVolume_unit" as FieldUniqueIdentifier
export const PRODUCT_NET_WEIGHT_VALUE =
  "_prod_netWeight_value" as FieldUniqueIdentifier
export const PRODUCT_NET_WEIGHT_UNIT =
  "_prod_netWeight_unit" as FieldUniqueIdentifier
export const PRODUCT_NET_VOLUME_VALUE =
  "_prod_netVolume_value" as FieldUniqueIdentifier
export const PRODUCT_NET_VOLUME_UNIT =
  "_prod_netVolume_unit" as FieldUniqueIdentifier
export const PRODUCT_PRODUCT_REFERENCE =
  "_prod_productReference" as FieldUniqueIdentifier
export const PRODUCT_PRODUCT_STATEMENT =
  "_prod_productStatement" as FieldUniqueIdentifier
export const REQUEST_DELIVERY_DETAILS =
  "_req_deliveryDetails" as FieldUniqueIdentifier
export const REQUEST_DELIVERY_DISPATCH_DETAILS =
  "_req_deliveryDispatchDetails" as FieldUniqueIdentifier
export const PRODUCT_SCIENTIFIC_NAMES =
  "_prod_scientificNames" as FieldUniqueIdentifier
export const PRODUCT_PRODUCTION_BATCH_IDS =
  "_prod_productionBatchIds" as FieldUniqueIdentifier
export const PRODUCT_INTENDED_USES =
  "_prod_intendedUses" as FieldUniqueIdentifier
export const PRODUCT_PKG_QUANTITY =
  "_prod_pkg_quantity" as FieldUniqueIdentifier
export const PRODUCT_PKG_PACKAGE_TYPE =
  "_prod_pkg_packageType" as FieldUniqueIdentifier
export const PRODUCT_PKG_ITEM_MARK =
  "_prod_pkg_itemMark" as FieldUniqueIdentifier
export const PRODUCT_PKG_ITEM_MARK_CAPTION =
  "_prod_pkg_itemMarkCaption" as FieldUniqueIdentifier
export const PRODUCT_PKG_PACKAGE_LEVEL =
  "_prod_pkg_packageLevel" as FieldUniqueIdentifier
export const PRODUCT_PKG_OUTERMOST_NUMBER_OF_PACKAGES =
  "_prod_pkg_outermostNumberOfPackages" as FieldUniqueIdentifier
export const PRODUCT_PKG_NOMINAL_GROSS_VOLUME_VALUE =
  "_prod_pkg_nominalGrossVolume_value" as FieldUniqueIdentifier
export const PRODUCT_PKG_NOMINAL_GROSS_VOLUME_UNIT =
  "_prod_pkg_nominalGrossVolume_unit" as FieldUniqueIdentifier
export const PRODUCT_PKG_NOMINAL_GROSS_WEIGHT_VALUE =
  "_prod_pkg_nominalGrossWeight_value" as FieldUniqueIdentifier
export const PRODUCT_PKG_NOMINAL_GROSS_WEIGHT_UNIT =
  "_prod_pkg_nominalGrossWeight_unit" as FieldUniqueIdentifier
export const PRODUCT_NZHSC = "_prod_NZHSC" as FieldUniqueIdentifier
export const PROCESS_TYPE_CODE = "_process_typeCode" as FieldUniqueIdentifier
export const PROCESS_START_DATE = "_process_startDate" as FieldUniqueIdentifier
export const PROCESS_SEQUENCE_NUMBER =
  "_prod_process_sequenceNumber" as FieldUniqueIdentifier
export const PROCESS_DURATION_VALUE =
  "_prod_process_duration_value" as FieldUniqueIdentifier
export const PROCESS_DURATION_UNIT =
  "_prod_process_duration_unit" as FieldUniqueIdentifier
export const PROCESS_TEMPERATURE_VALUE =
  "_prod_process_temperature_value" as FieldUniqueIdentifier
export const PROCESS_TEMPERATURE_UNIT =
  "_prod_process_temperature_unit" as FieldUniqueIdentifier
export const PROCESS_CONCENTRATION =
  "_prod_process_concentration" as FieldUniqueIdentifier
export const PROCESS_ADDITIONAL_INFO =
  "_prod_process_additionalTreatment" as FieldUniqueIdentifier
export const PROCESS_END_DATE = "_process_endDate" as FieldUniqueIdentifier
export const PROCESS_DATE_OVERRIDE =
  "_process_dateOverride" as FieldUniqueIdentifier
export const PROCESS_PREMISES_IDENTIFIER =
  "_process_nzPremisesIdentifier" as FieldUniqueIdentifier
export const PROCESS_FOREIGN_PREMISES_IDENTIFIER =
  "_process_foreignPremisesIdentifier" as FieldUniqueIdentifier
export const PROCESS_FOREIGN_PREMISES_NAME =
  "_process_foreignPremisesName" as FieldUniqueIdentifier
export const PROCESS_FOREIGN_PREMISES_COUNTRY =
  "_process_foreignPremisesCountry" as FieldUniqueIdentifier
export const PROCESS_FOREIGN_PREMISES_NAME_OVERRIDE =
  "_process_foreignPremisesNameOverride" as FieldUniqueIdentifier
export const PROCESS_CHEMICAL_DETAIL =
  "_prod_process_chemical" as FieldUniqueIdentifier
export const PRODUCT_SOURCE_ID = "_prod_source_id" as FieldUniqueIdentifier
export const PRODUCT_SOURCE_ITEM_DESCRIPTION =
  "_prod_source_item_description" as FieldUniqueIdentifier
export const PRODUCT_SOURCE_TRANSFER_REQUEST_ID =
  "_prod_source_transfer_request_id" as FieldUniqueIdentifier
export const PRODUCT_SOURCE_TRANSFER_NUMBER =
  "_prod_source_transfer_number" as FieldUniqueIdentifier
export const PRODUCT_SOURCE_REQUEST_NUMBER =
  "_prod_source_requestNumber" as FieldUniqueIdentifier
export const PRODUCT_SOURCE_SEQUENCE_NUMBER =
  "_prod_source_sequenceNumber" as FieldUniqueIdentifier
export const DOC_REFERENCE_NUMBER =
  "_doc_referenceNumber" as FieldUniqueIdentifier
export const DOC_COMMENTS = "_doc_comments" as FieldUniqueIdentifier
export const REQUEST_STATUS = "_req_status_code" as FieldUniqueIdentifier
export const REQUEST_STATUS_LABEL = "_req_status_label" as FieldUniqueIdentifier
export const REQUEST_REASON_FOR_STATUS_CHANGE =
  "_req_reasonForStateChange" as FieldUniqueIdentifier
export const REQUEST_TRANSIT_COUNTRIES =
  "_req_transitCountries" as FieldUniqueIdentifier
export const PARTY_CUSTOM_TRANSIT_AGENT_ID =
  "_party_customsTransitAgent_identifier" as FieldUniqueIdentifier
export const PARTY_CUSTOM_TRANSIT_AGENT_NAME =
  "_party_customsTransitAgent_name" as FieldUniqueIdentifier
export const PARTY_CUSTOM_TRANSIT_AGENT_ADDRESS_LINES =
  "_party_customsTransitAgent_addressLines" as FieldUniqueIdentifier
export const PARTY_CUSTOM_TRANSIT_AGENT_CITY =
  "_party_customsTransitAgent_city" as FieldUniqueIdentifier
export const PARTY_CUSTOM_TRANSIT_AGENT_POSTCODE =
  "_party_customsTransitAgent_postCode" as FieldUniqueIdentifier
export const PARTY_CUSTOM_TRANSIT_AGENT_STATE =
  "_party_customsTransitAgent_state" as FieldUniqueIdentifier
export const PARTY_CUSTOM_TRANSIT_AGENT_COUNTRY =
  "_party_customsTransitAgent_country_id" as FieldUniqueIdentifier
export const REQUEST_REQUESTOR_REFERENCE =
  "_req_requestorReference" as FieldUniqueIdentifier
export const PROCESS_ID = "processId" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_MARKING_OF_SEAL_ON_TRANSPORT_CONTAI =
  "_req_attr_Markingofsealontransportcontai" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_NAME_REGISTRATION_AND_ADDRESS_SE =
  "_req_attr_Nameregistrationandaddressofse" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_NUMBER_OF_DOSES =
  "_req_attr_NumberofDoses" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_NUMBER_OF_EMBRYOS =
  "_req_attr_NumberofEmbryos" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_NUMBER_OF_PACKAGES_CONTAINER =
  "_req_attr_NumberofPackagescontainers" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_PLACE_AND_DATE_OF_QUARANTINE =
  "_req_attr_PlaceandDateofQuarantine" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_SUMMARY_OF_RECORDS_FOR =
  "_req_attr_Summaryofrecordsfor" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_EXP_REF =
  "_req_attr_ExpRef" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_CONTRACT_NO =
  "_req_attr_contractNo" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_ISSUING_AUTHORITY =
  "_req_attr_issuingAuthority" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_HALAL_AUDITOR =
  "_req_attr_halalAuditor" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_HALAL_CERTIFICATE_NO =
  "_req_attr_halalCertificateNumber" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_ISSUING_AUTHORITY_1 =
  "_req_attr_issuingAuthority1" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_BRAND_NAME =
  "_req_attr_brandName" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_CATEGORY_OF_GOODS =
  "_req_attr_categoryOfGoods" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_ADDRESS =
  "_req_attr_address" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_SANITARY_CERTIFICATE_NUMBER =
  "_req_attr_SanitaryCertificateNumber" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_CN_INSPECTION =
  "_req_attr_CNInspection" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_ADMINISTRATIVE_TERRITORIAL_UNIT =
  "_req_attr_administrativeTerritorialUnit" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_SCOPE =
  "_req_attr_Scope" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_TRANSPORT =
  "_req_attr_Transport" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_MANUFACTURER_REGISTRATION =
  "_req_attr_ManufacturerRegistration" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_MANUFACTURING_PREMISES =
  "_req_attr_Manufacturingpremisesname" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_PRODUCT_DESCRIPTION =
  "_req_attr_productDescription" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_PURPOSE_BR =
  "_req_attr_PurposeBR" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_LOT = "_req_attr_lot" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_NET_WEIGHT_IN_WORDS =
  "_req_attr_NetWeightInWords" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_EU_COMMODITY_CODE =
  "_req_attr_EUCommodityCode" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_EU_TRANSIT =
  "_req_attr_EUTransit" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_EXPORTER_REF =
  "_req_attr_Exporter_Ref" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_FOR_IMPORT_OR_ADMISSION =
  "_req_attr_Forimportoradmission" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_FOR_TOTAL_GROSS_WEIGHT =
  "_req_attr_TotalGrossWeight" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_FOR_TOTAL_QUANTITY =
  "_req_attr_TotalQuantity" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_ED_REFERENCE =
  "_req_attr_EDReference" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_EXPORTER_SIGNATURE =
  "_req_attr_ExporterSignature" as FieldUniqueIdentifier
export const REQUEST_EXHAUSTED = "_req_exhausted" as FieldUniqueIdentifier
export const REQUEST_LAST_MODIFIED_AT =
  "_req_nsi_lastModifiedAt" as FieldUniqueIdentifier
export const REQUEST_DECLARATION_STATEMENT_CODE =
  "_decl_code" as FieldUniqueIdentifier
export const REQUEST_DECLARATION_STATEMENT_VALUE =
  "_decl_userInput_value" as FieldUniqueIdentifier
export const PRODUCT_CLS_TYPE = "_prod_cls_type" as FieldUniqueIdentifier
export const PRODUCT_CLS_VALUE = "_prod_cls_value" as FieldUniqueIdentifier
export const PRODUCT_CLS_PROD_QUANTITY =
  "_prod_cls_ProdQuantity" as FieldUniqueIdentifier
export const PRODUCT_ITEM_SEQUENCE_NUMBER =
  "_prod_sequenceNumber" as FieldUniqueIdentifier
export const IS_SOURCE_PROCESS = "isSourceProcess" as FieldUniqueIdentifier
export const PARTY_VERIFYING_AGENCY =
  "_party_verifyingAgency_identifier" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_ORG_CERT_REF_CODE =
  "_req_attr_orgCertRefCode" as FieldUniqueIdentifier
export const PHYTO_SCHEME_CONTROL =
  "_req_attr_phytoSchemeControl" as FieldUniqueIdentifier
export const PHYTO_STATEMENT_OF_RELABELLING =
  "_req_attr_statementOfRelabelling" as FieldUniqueIdentifier
export const PHYTO_ATTRIBUTE_CERT_SUB_TYPE =
  "_req_attr_phytoCertSubType" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_PHYTO_CERTIFICATE_NUMBER =
  "_req_attr_phytoCertificateNumber" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_GROWERS =
  "_prod_phytoGrowers" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_ENDPOINT_INSPECTION_REF =
  "_prod_phytoEndPointInspectionRef" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_SUPPLIER =
  "_prod_phytoSupplier" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_INSPECTION_LOCATION =
  "_prod_phytoInspectionLocation" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_OBSERVATIONS =
  "_prod_phytoObservations" as FieldUniqueIdentifier
export const PARTY_THIRD_PARTY_CONSIGNOR_NAME =
  "_party_thirdPartyConsignor_name" as FieldUniqueIdentifier
export const PARTY_THIRD_PARTY_CONSIGNOR_ADDRESS_LINES =
  "_party_thirdPartyConsignor_addressLines" as FieldUniqueIdentifier
export const PARTY_THIRD_PARTY_CONSIGNOR_CITY =
  "_party_thirdPartyConsignor_city" as FieldUniqueIdentifier
export const PARTY_THIRD_PARTY_CONSIGNOR_POST_CODE =
  "_party_thirdPartyConsignor_postCode" as FieldUniqueIdentifier
export const PARTY_THIRD_PARTY_CONSIGNOR_COUNTRY =
  "_party_thirdPartyConsignor_country" as FieldUniqueIdentifier
export const REQUEST_FREE_TEXT_DECLARATION =
  "_req_freeTextDeclaration" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_INSPECTION_DATE =
  "_req_inspectionDate" as FieldUniqueIdentifier
export const DOC_FILE_NAME = "_doc_fileName" as FieldUniqueIdentifier
export const DOC_CONTENT_TYPE = "_doc_contentType" as FieldUniqueIdentifier
export const PRODUCT_GROUP_NUMBER =
  "_prod_tradeLineGroupNumber" as FieldUniqueIdentifier
export const REQUEST_CERTIFICATE_TYPE =
  "_req_certificateType" as FieldUniqueIdentifier
export const REQUEST_CERTIFICATE_NUMBER =
  "_req_certificateNumber" as FieldUniqueIdentifier
export const REQUEST_UNIQUE_CONSIGNMENT_REFERENCE =
  "_req_uniqueConsignmentReference" as FieldUniqueIdentifier
export const REQUEST_REPLACES_CERTIFICATE_NUMBER =
  "_req_replaces_certificateNumber" as FieldUniqueIdentifier
export const REQUEST_REPLACES_APPROVED_AT =
  "_req_replaces_approvedAt" as FieldUniqueIdentifier
export const REQUEST_REPLACED_BY_CERTIFICATE_NUMBER =
  "_req_replacedBy_certificateNumber" as FieldUniqueIdentifier
export const TEMPLATE_TITLE = "_template_title" as FieldUniqueIdentifier
export const TEMPLATE_SUBTITLE = "_template_subtitle" as FieldUniqueIdentifier
export const PARTY_CERTIFYING_AGENCY_IDENTIFIER =
  "_party_certifyingAgency_identifier" as FieldUniqueIdentifier
export const PARTY_TD_CONSIGNOR_NAME =
  "_party_tdConsignor_name" as FieldUniqueIdentifier
export const PARTY_TD_CONSIGNOR_CITY =
  "_party_tdConsignor_city" as FieldUniqueIdentifier
export const PARTY_CONSIGNOR_NAME =
  "_party_consignor_name" as FieldUniqueIdentifier
export const PARTY_CONSIGNOR_CITY =
  "_party_consignor_city" as FieldUniqueIdentifier
export const PARTY_CONSIGNOR_ADDRESS_LINES =
  "_party_consignor_addressLines" as FieldUniqueIdentifier
export const PARTY_CONSIGNOR_POST_CODE =
  "_party_consignor_postCode" as FieldUniqueIdentifier
export const PARTY_CONSIGNOR_STATE =
  "_party_consignor_state" as FieldUniqueIdentifier
export const PARTY_CONSIGNOR_COUNTRY =
  "_party_consignor_country" as FieldUniqueIdentifier
export const PARTY_TD_CONSIGNEE_NAME =
  "_party_tdConsignee_name" as FieldUniqueIdentifier
export const PARTY_TD_CONSIGNEE_CITY =
  "_party_tdConsignee_city" as FieldUniqueIdentifier
export const REQUEST_NSI_LAST_MODIFIED_BY_NAME =
  "_req_nsi_lastModifiedBy_name" as FieldUniqueIdentifier
export const REQUEST_NSI_SUBMITTED_AT =
  "_req_nsi_submittedAt" as FieldUniqueIdentifier
export const REQUEST_NSI_SUBMITTED_BY_NAME =
  "_req_nsi_submittedBy_name" as FieldUniqueIdentifier
export const REQUEST_NSI_APPROVED_AT =
  "_req_nsi_approvedAt" as FieldUniqueIdentifier
export const REQUEST_NSI_APPROVED_BY_NAME =
  "_req_nsi_approvedBy_name" as FieldUniqueIdentifier
export const REQUEST_NSI_APPROVED_BY_TITLE =
  "_req_nsi_approvedBy_title" as FieldUniqueIdentifier
export const REQUEST_NSI_APPROVED_BY_QUALIFICATIONS =
  "_req_nsi_approvedBy_qualifications" as FieldUniqueIdentifier
export const REQUEST_NSI_APPROVED_BY_CITY =
  "_req_nsi_approvedBy_city" as FieldUniqueIdentifier
export const REQUEST_NSI_APPROVED_BY_COUNTRY =
  "_req_nsi_approvedBy_country" as FieldUniqueIdentifier
export const REQUEST_NSI_AUDITED_AT =
  "_req_nsi_auditedAt" as FieldUniqueIdentifier
export const REQUEST_NSI_AUDITED_BY_NAME =
  "_req_nsi_auditedBy_name" as FieldUniqueIdentifier
export const REQUEST_NSI_AUDIT_COMMENTS =
  "_req_nsi_auditComments" as FieldUniqueIdentifier
export const REQUEST_SHIP_STORES = "_req_shipStores" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_PHYTO_LOT_REFERENCE_NUMBER =
  "_req_attr_phytoLotReferenceNumber" as FieldUniqueIdentifier
export const REQUEST_ATTRIBUTE_STATEMENT_OF_RELABELLING =
  "_req_attr_statementOfRelabelling" as FieldUniqueIdentifier
export const PRODUCT_PRODUCT_NUMBER =
  "_prod_productNumber" as FieldUniqueIdentifier
export const PRODUCT_REQUEST_NUMBER =
  "_prod_requestNumber" as FieldUniqueIdentifier
export const PRODUCT_EXHAUSTED = "_prod_exhausted" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_PACKHOUSE =
  "_prod_phytoPackhouse" as FieldUniqueIdentifier
export const PRODUCT_OVERSEAS_CERT_IDS =
  "_prod_overseasCertIDs" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_IS_ORIGINAL_CERT =
  "_prod_phytoIsOriginalCert" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_IS_ORIGINAL_CONTAINER =
  "_prod_phytoIsOriginalContainer" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_IS_REPACKED =
  "_prod_phytoIsRepacked" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_IS_INSPECTION_ON_ORIGINAL =
  "_prod_phytoIsInspectionOnOriginal" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_SEED_LOT_REFERENCE =
  "_prod_phytoSeedLotReference" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_PERCENTAGE_BY_WEIGHT =
  "_prod_phytoPercentageByWeight" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_SEED_LOT_APPROVAL =
  "_prod_phytoSeedLotApproval" as FieldUniqueIdentifier
export const PRODUCT_PHYTO_MIXTURE_NAME =
  "_prod_phytoMixtureName" as FieldUniqueIdentifier
export const PRODUCT_NET_UNITS_VALUE =
  "_prod_netUnits_value" as FieldUniqueIdentifier
export const PRODUCT_NET_UNITS_UNIT =
  "_prod_netUnits_unit" as FieldUniqueIdentifier
export const PRODUCT_PRODUCT_DECLARATION =
  "_prod_productDeclaration" as FieldUniqueIdentifier
export const PRODUCT_TEMPERATURE = "_prod_temperature" as FieldUniqueIdentifier
export const DOC_DOCUMENT_TYPE = "_doc_documentType" as FieldUniqueIdentifier
export const DOC_DOCUMENT_REFERENCE_NUMBER =
  "_doc_documentReferenceNumber" as FieldUniqueIdentifier
export const DOC_PRIMARY_OPERATOR =
  "_doc_primaryOperator" as FieldUniqueIdentifier
export const DOC_INTENDED_OPERATORS =
  "_doc_intendedOperators" as FieldUniqueIdentifier
export const DOC_COUNTRY_OF_ORIGIN =
  "_doc_countryOfOrigin" as FieldUniqueIdentifier
export const DOC_PRODUCT_DESCRIPTION =
  "_doc_productDescription" as FieldUniqueIdentifier
export const DOC_IDENTIFICATION_MARKS =
  "_doc_identificationMarks" as FieldUniqueIdentifier
export const DOC_FOREIGN_PREMISES_IDENTIFIER =
  "_doc_foreignPremisesIdentifier" as FieldUniqueIdentifier
export const DOC_FOREIGN_PREMISES_NAME =
  "_doc_foreignPremisesName" as FieldUniqueIdentifier
export const DOC_NZ_PREMISES_IDENTIFIER =
  "_doc_nzPremisesIdentifier" as FieldUniqueIdentifier
export const DOC_PRODUCTION_DATES_FROM =
  "_doc_productionDates_from" as FieldUniqueIdentifier
export const DOC_PRODUCTION_DATES_TO =
  "_doc_productionDates_to" as FieldUniqueIdentifier
export const DOC_REASON_FOR_UPDATE =
  "_doc_reasonForUpdate" as FieldUniqueIdentifier
export const NSI_STATIONERY_SERIAL_NUMBER =
  "_nsi_stationerySerialNumber" as FieldUniqueIdentifier
export const NSI_STATIONERY_SN_UPDATED_AT =
  "_nsi_stationerySNUpdatedAt" as FieldUniqueIdentifier
export const NSI_TOTAL_PACKAGES = "_nsi_totalPackages" as FieldUniqueIdentifier
export const REQUEST_REASON_FOR_CHANGE_CATEGORIES =
  "_req_reasonForChangeCategories" as FieldUniqueIdentifier
export const REQUEST_OVERSEAS_AUTHORITY_INVOLVED =
  "_req_overseasAuthorityInvolved" as FieldUniqueIdentifier
export const TRANSFER_PRODUCT_SUPPORTING_DOC_ID =
  "_transfer_product_supportingDocument_id" as FieldUniqueIdentifier
export const TRANSFER_PRODUCT_SUPPORTING_DOC_IS_INHERITED =
  "_transfer_product_supportingDocument_isInherited" as FieldUniqueIdentifier
export const PROD_CLS_PERSONDETAILS =
  "_prod_cls_Persondetails" as FieldUniqueIdentifier
export const PROD_CLS_CERTIFICATE_REFERENCE =
  "_prod_cls_Certificatereference" as FieldUniqueIdentifier
